(function () {
  const LOG_TAG = '[caixa-mista-guard]';
  let isRunning = false;
  let scheduled = false;

  function log() {
    if (window.__cmCartGuardDebug === false) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_TAG);
    console.log.apply(console, args);
  }

  async function getCart() {
    const r = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    return r.json();
  }

  function groupByBundle(cart) {
    const groups = {};
    (cart.items || []).forEach((item) => {
      const props = item.properties || {};
      const bundleId = props._cm_bundle;
      if (!bundleId) return;
      if (!groups[bundleId]) groups[bundleId] = { parents: [], children: [] };
      if (props._cm_role === 'parent') {
        groups[bundleId].parents.push(item);
      } else if (props._cm_role === 'child') {
        groups[bundleId].children.push(item);
      }
    });
    return groups;
  }

  function sumQuantities(items) {
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  // Returns the line-item keys that must be removed to keep every caixa-mista
  // bundle valid. A bundle is only valid when it has exactly one parent (qty 1)
  // and the sum of its children equals the size encoded on the parent (6 or 12).
  // Any drift (orphan children, duplicated parent, tampered quantities) is
  // treated as an underpriced/invalid bundle and the whole group is dropped.
  function findInvalidKeys(cart) {
    const groups = groupByBundle(cart);
    const keys = [];
    Object.keys(groups).forEach((id) => {
      const g = groups[id];

      // Orphan children: the bundle lost its parent -> drop the children.
      if (g.parents.length === 0) {
        g.children.forEach((c) => keys.push(c.key));
        return;
      }

      // A bundle id must map to a single parent line.
      if (g.parents.length > 1) {
        g.parents.forEach((p) => keys.push(p.key));
        g.children.forEach((c) => keys.push(c.key));
        return;
      }

      const parent = g.parents[0];
      const props = parent.properties || {};
      const expectedSize = parseInt(props._cm_size, 10);

      // Legacy bundles added before _cm_size existed can't be size-checked;
      // leave them untouched beyond the orphan rule handled above.
      if (isNaN(expectedSize)) return;

      // Parent must be a single box and the children must fill it exactly.
      const childCount = sumQuantities(g.children);
      if (parent.quantity !== 1 || childCount !== expectedSize) {
        log('invalid bundle', id, 'parentQty=', parent.quantity, 'children=', childCount, 'expected=', expectedSize);
        g.parents.forEach((p) => keys.push(p.key));
        g.children.forEach((c) => keys.push(c.key));
      }
    });
    return keys;
  }

  function getMiniCart() {
    return document.querySelector('mini-cart');
  }

  // The full cart page renders a <cart-items> that is NOT inside the drawer.
  // The drawer's own <cart-items> lives inside <mini-cart>. We use this to tell
  // "user is on /cart" apart from "drawer is present", so we can hard-refresh
  // the page when we mutate the cart there (the guard fetches sections for the
  // drawer only, so the cart-page list would otherwise stay stale).
  function getCartPageRoot() {
    const all = document.querySelectorAll('cart-items');
    for (let i = 0; i < all.length; i += 1) {
      if (!all[i].closest('mini-cart') && !all[i].closest('cart-drawer')) {
        return all[i];
      }
    }
    return null;
  }

  function getSectionIds() {
    const miniCart = getMiniCart();
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map((s) => s.id);
    }
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  async function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
  }

  async function removeLinesViaChange(keys) {
    const sectionIds = getSectionIds();
    let last = null;
    for (const key of keys) {
      const body = {
        id: key,
        quantity: 0,
        sections: sectionIds,
        sections_url: window.location.pathname
      };
      const res = await postJson('/cart/change.js', body);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(LOG_TAG, '/cart/change.js failed', res.status, text, 'key=', key);
        continue;
      }
      last = await res.json();
    }
    return last;
  }

  async function removeLinesAtomically(keys) {
    if (!keys || !keys.length) return null;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    const body = {
      updates,
      sections: getSectionIds(),
      sections_url: window.location.pathname
    };
    log('POST /cart/update.js', body);
    const res = await postJson('/cart/update.js', body);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.warn(LOG_TAG, '/cart/update.js failed, falling back to /cart/change.js', res.status, text);
      return removeLinesViaChange(keys);
    }
    return res.json();
  }

  function updateIconBubble(state) {
    if (!state || !state.sections) return;
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      const el = document.getElementById(id);
      const html = state.sections[id];
      if (!el || !html) return;
      try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const inner = parsed.querySelector('.shopify-section');
        if (inner) el.innerHTML = inner.innerHTML;
      } catch (e) {
        console.error(LOG_TAG, 'icon-bubble update failed', e);
      }
    });
  }

  function applyCartState(state, opts) {
    if (!state) return;
    const openDrawer = opts && opts.openDrawer;
    const miniCart = getMiniCart();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'caixa-mista-guard', cart: state });
    }

    const cartItems = document.querySelector('cart-items');
    if (!cartItems) updateIconBubble(state);

    if (openDrawer && miniCart && typeof miniCart.renderContents === 'function') {
      if (miniCart.classList.contains('is-empty') && state.item_count > 0) {
        miniCart.classList.remove('is-empty');
      }
      miniCart.renderContents(state);
    }
  }

  async function enforce() {
    if (isRunning) {
      scheduled = true;
      return;
    }
    isRunning = true;
    try {
      const cart = await getCart();
      const invalidKeys = findInvalidKeys(cart);
      log('enforce: invalid lines=', invalidKeys.length);
      if (invalidKeys.length === 0) return;
      const state = await removeLinesAtomically(invalidKeys);
      applyCartState(state, { openDrawer: false });
    } catch (err) {
      console.error(LOG_TAG, 'enforce failed', err);
    } finally {
      isRunning = false;
      if (scheduled) {
        scheduled = false;
        setTimeout(enforce, 50);
      }
    }
  }

  async function removeBundle(bundleId) {
    if (!bundleId) return;
    if (isRunning) {
      scheduled = true;
      return;
    }
    isRunning = true;
    try {
      const cart = await getCart();
      const matching = (cart.items || []).filter((item) => {
        const props = item.properties || {};
        return props._cm_bundle === bundleId;
      });
      log('removeBundle', bundleId, 'matches=', matching.length);
      if (!matching.length) return;
      const keys = matching.map((i) => i.key);
      const state = await removeLinesAtomically(keys);
      // On the cart page the guard only refreshes drawer sections, so reload to
      // reflect the removed bundle in the visible line-item list.
      if (getCartPageRoot()) {
        window.location.reload();
        return;
      }
      applyCartState(state, { openDrawer: true });
    } catch (err) {
      console.error(LOG_TAG, 'removeBundle failed', err);
    } finally {
      isRunning = false;
      if (scheduled) {
        scheduled = false;
        setTimeout(enforce, 50);
      }
    }
  }

  function interceptParentRemoval(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    const removeBtn = target.closest('cart-remove-button');
    if (!removeBtn) return;
    const parentContainer = removeBtn.closest('[data-cm-role="parent"]');
    if (!parentContainer) return;
    const bundleId = parentContainer.dataset.cmBundle;
    if (!bundleId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    log('intercepted parent X click, bundle=', bundleId);
    removeBundle(bundleId);
  }

  function init() {
    document.addEventListener('click', interceptParentRemoval, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        if (payload && payload.source === 'caixa-mista-guard') return;
        setTimeout(enforce, 80);
      });
    }

    document.addEventListener('cart:updated', (e) => {
      const detail = e && e.detail;
      if (detail && detail.source === 'caixa-mista-guard') return;
      setTimeout(enforce, 80);
    });

    enforce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
